import type { AgentBuilderScheduleTriggerType } from "@app/components/agent_builder/AgentBuilderFormContext";
import { ScheduleEditionScheduler } from "@app/components/agent_builder/triggers/schedule/ScheduleEditionScheduler";
import { TriggerPodSelector } from "@app/components/agent_builder/triggers/TriggerPodSelector";
import type { TriggerViewsSheetFormValues } from "@app/components/agent_builder/triggers/triggerViewsSheetFormSchema";
import type { LightWorkspaceType } from "@app/types/user";
import {
  ContentMessage,
  Input,
  Label,
  Separator,
  SliderToggle,
  TextArea,
} from "@dust-tt/sparkle";
// biome-ignore lint/correctness/noUnusedImports: ignored using `--suppress`
import React from "react";
import { useController, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

interface ScheduleEditionNameInputProps {
  isEditor: boolean;
}

function ScheduleEditionNameInput({ isEditor }: ScheduleEditionNameInputProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController({ control, name: "schedule.name" });

  return (
    <div className="flex-1 space-y-1">
      <Label htmlFor="trigger-name">
        {t("builder.scheduleNameLabel", "Name")}
      </Label>
      <Input
        id="trigger-name"
        placeholder={t("builder.scheduleNamePlaceholder", "Enter trigger name")}
        disabled={!isEditor}
        {...field}
        isError={!!error}
        message={error?.message}
        messageStatus="error"
      />
    </div>
  );
}

interface ScheduleEditionStatusToggleProps {
  isEditor: boolean;
}

function ScheduleEditionStatusToggle({
  isEditor,
}: ScheduleEditionStatusToggleProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field: { value: status, onChange: setStatus },
  } = useController({ control, name: "schedule.status" });

  const isEnabled = status === "enabled";

  return (
    <div className="space-y-1">
      <Label>{t("builder.scheduleStatusLabel", "Status")}</Label>
      <div className="flex flex-row items-center gap-2">
        <span className="w-16">
          {isEnabled
            ? t("builder.scheduleStatusEnabled", "Enabled")
            : t("builder.scheduleStatusDisabled", "Disabled")}
        </span>
        <SliderToggle
          disabled={!isEditor}
          selected={isEnabled}
          onClick={() => setStatus(isEnabled ? "disabled" : "enabled")}
        />
      </div>
    </div>
  );
}

interface ScheduleEditionMessageInputProps {
  isEditor: boolean;
}

function ScheduleEditionMessageInput({
  isEditor,
}: ScheduleEditionMessageInputProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const { field } = useController({ control, name: "schedule.customPrompt" });

  return (
    <div className="space-y-1">
      <Label htmlFor="schedule-custom-prompt">
        {t("builder.scheduleMessageLabel", "Message (optional)")}
      </Label>
      <p className="text-sm text-muted-foreground">
        {t(
          "builder.scheduleMessageDescription",
          "Message for the agent when the trigger runs."
        )}
      </p>
      <TextArea
        id="schedule-custom-prompt"
        minRows={4}
        disabled={!isEditor}
        {...field}
      />
    </div>
  );
}

interface ScheduleEditionPodSelectorProps {
  isEditor: boolean;
  owner: LightWorkspaceType;
}

function ScheduleEditionPodSelector({
  isEditor,
  owner,
}: ScheduleEditionPodSelectorProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const { field } = useController({ control, name: "schedule.spaceId" });

  return (
    <div className="space-y-1">
      <Label>
        {t(
          "builder.schedulePodSelectorLabel",
          "Where to create this conversation? (optional)"
        )}{" "}
      </Label>
      <p className="text-sm text-muted-foreground">
        {t(
          "builder.schedulePodSelectorDescription",
          "Run this trigger's conversation inside a Pod instead."
        )}
      </p>
      <TriggerPodSelector
        owner={owner}
        value={field.value}
        onChange={field.onChange}
        disabled={!isEditor}
      />
    </div>
  );
}

interface ScheduleEditionSheetContentProps {
  owner: LightWorkspaceType;
  trigger: AgentBuilderScheduleTriggerType | null;
  isEditor: boolean;
}

export function ScheduleEditionSheetContent({
  owner,
  trigger,
  isEditor,
}: ScheduleEditionSheetContentProps) {
  const { t } = useTranslation();
  return (
    <>
      {trigger && !isEditor && (
        <ContentMessage variant="info">
          <Trans
            i18nKey="builder.scheduleCannotEditManagedBy"
            defaults="You cannot edit this schedule. It is managed by <0>{{editorName}}</0>."
            values={{
              editorName:
                trigger.editorName ??
                t("builder.scheduleAnotherUser", "another user"),
            }}
            components={[<span key="0" className="font-semibold" />]}
          />
        </ContentMessage>
      )}
      <div className="space-y-8">
        {" "}
        <div className="flex flex-row items-center justify-between gap-4">
          <ScheduleEditionNameInput isEditor={isEditor} />
          <ScheduleEditionStatusToggle isEditor={isEditor} />
        </div>
        <ScheduleEditionScheduler isEditor={isEditor} owner={owner} />
        <Separator />
        <ScheduleEditionMessageInput isEditor={isEditor} />
        <Separator />
        <ScheduleEditionPodSelector isEditor={isEditor} owner={owner} />
      </div>
    </>
  );
}
